import { StreamRotate } from '@anm/api/modules/stream';
import { SelectOption } from '@anm/components/select/Select';
import { DestinationWithStreamMeta } from '@anm/components/stream/StreamItem';
import isJSON from '@anm/helpers/is/isJSON';
import { useEffect, useRef, useState } from 'react';

import { ChangeDataProps, PreviewUpdateProps } from './types';

import {
  FB_PRIVACY_FRIENDS,
  FB_PRIVACY_PRIVATE,
  FB_PRIVACY_PUBLIC,
  YT_PRIVACY_PRIVATE,
  YT_PRIVACY_PUBLIC,
  YT_PRIVACY_UNLISTED
} from '@anm/data/streaming/destinationsCaps';

const FB_PRIVACY_OPTIONS = [
  {
    value: FB_PRIVACY_PRIVATE,
    label: 'Only me'
  },
  {
    value: FB_PRIVACY_FRIENDS,
    label: 'Friends'
  },
  {
    value: FB_PRIVACY_PUBLIC,
    label: 'Public'
  }
];

const YT_PRIVACY_OPTIONS = [
  {
    value: YT_PRIVACY_PRIVATE,
    label: 'Private'
  },
  {
    value: YT_PRIVACY_UNLISTED,
    label: 'Unlisted'
  },
  {
    value: YT_PRIVACY_PUBLIC,
    label: 'Public'
  }
];

export const useDestinationPrivacy = (destination: DestinationWithStreamMeta, initialPrivacy?: string) => {
  const isFb = destination.network === 'FACEBOOK';
  const privacyOptions = isFb ? FB_PRIVACY_OPTIONS : YT_PRIVACY_OPTIONS;

  const parsedPrivacy = initialPrivacy && isJSON(initialPrivacy) ? JSON.parse(initialPrivacy).value : initialPrivacy;

  const defaultPrivacy = parsedPrivacy
    ? {
        label: privacyOptions.find(p => p.value === parsedPrivacy)?.label || '',
        value: parsedPrivacy
      }
    : privacyOptions[2];
  const [privacy, setPrivacy] = useState<SelectOption>(defaultPrivacy);

  return [privacy, privacyOptions, setPrivacy] as const;
};

export const useChangeData = ({
  name,
  privacy,
  defaultName,
  description,
  defaultPrivacy,
  defaultDescription
}: ChangeDataProps) => {
  const isUploaded = useRef(false);
  const [isFieldsChanged, setIsFieldsChanged] = useState(false);

  useEffect(() => {
    const isSameTitle = name === defaultName;
    const isSameDescription = description === defaultDescription;
    const isSamePrivacy =
      (!defaultPrivacy && [FB_PRIVACY_OPTIONS[2].value, YT_PRIVACY_OPTIONS[2].value].includes(privacy)) ||
      defaultPrivacy?.includes(privacy);

    setIsFieldsChanged(!isSameTitle || !isSameDescription || !isSamePrivacy);
  }, [name, privacy, description]);

  return [isUploaded, isFieldsChanged] as const;
};

export const usePreviewUpdate = ({ isUploaded, previewSrc, savedPreview }: PreviewUpdateProps) => {
  const [src, setSrc] = useState(previewSrc);

  useEffect(() => {
    let newSrc = '';

    if (previewSrc?.includes('blob')) newSrc = previewSrc;
    else if (isUploaded) {
      newSrc = previewSrc || savedPreview || '';
    } else newSrc = savedPreview || previewSrc || '';

    setSrc(newSrc);
  }, [previewSrc, savedPreview]);

  const hasUploadError = previewSrc?.includes('blob');

  return [src, hasUploadError] as const;
};

export const useStreamRotation = (defaultRotation?: StreamRotate) => {
  // null for maximize horizontal
  const [rotate, setRotate] = useState<StreamRotate>(defaultRotation || 'vertical');

  return [rotate, setRotate] as const;
};
